import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslationHandleService } from '../../../../core/translate/translation-handle.service';
import { Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { SecurityType } from '../../../../shared/security-type.enum';
import { CustomMapsService } from '../custom-maps.service';
import { IdentityService } from '../../../../services/identity.service';
import { WmsFieldsRequestData } from '../interfaces/wms-fields-request-data';
import { LayerDependsWmsFieldsData } from '../interfaces/layer-depends-wms-fields-data';
import { LayerFieldOptionWms } from '../interfaces/layer-field-option-wms';
import { HttpErrorResponse } from '@angular/common/http';

const PHRASES_FOR_TRANSLATE = [
  {
    public: 'sources.details.Public',
  },
  {
    password: 'sources.details.Password',
  },
];

@Component({
  selector: 'mee-wms-custom-map-form',
  templateUrl: './wms-custom-map-form.component.html',
  styleUrls: ['./wms-custom-map-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WmsCustomMapFormComponent implements OnInit, OnDestroy {
  @Input() selectedCustomMap: any; //  WmsMapProvider

  @Output() selectedLayerChanged = new EventEmitter<LayerFieldOptionWms>();

  @Output() onWMSDataChanged: EventEmitter<any> = new EventEmitter(); // <WmsMapProvider>

  wmsFormGroup: UntypedFormGroup;

  securityTypes: Array<{ name: string; value: number }>;

  translationMap: Map<string, string>;

  isShowLayersDependsFields = false;

  styles: Array<string>;

  layersOptions: Array<LayerFieldOptionWms>;

  filteredLayersOptions$: Observable<LayerFieldOptionWms[]>;

  imageFormats: Array<string>;

  isLayersDependsFieldsDataLoading = false;

  isGetLayersError = false;

  errorText: string;

  private localIsUpdateMode: boolean;

  private destroy$ = new Subject<void>();

  @Input() set isUpdateMode(isUpdateMode) {
    this.onWMSDataChanged.next({ data: this.selectedCustomMap });

    this.localIsUpdateMode = isUpdateMode;
  }

  get isUpdateMode() {
    return this.localIsUpdateMode;
  }

  get isSuperAdmin() {
    return this.identityService.isSuperAdmin();
  }

  constructor(
    private identityService: IdentityService,
    private customMapsService: CustomMapsService,
    private translationHandleService: TranslationHandleService,
    private cdr: ChangeDetectorRef,
  ) {
    translationHandleService.setPhrasesForTranslate(PHRASES_FOR_TRANSLATE);

    translationHandleService
      .getTranslationMap()
      .pipe(takeUntil(this.destroy$))
      .subscribe(translationMap => {
        this.translationMap = translationMap;
      });
  }

  ngOnInit() {
    this.initWMSMapForm();

    this.onChangeFormValue();

    if (this.isUpdateMode) {
      this.wmsFormGroup.patchValue(this.selectedCustomMap);

      this.getFieldsValue();

      let customMapCredentials;

      if (this.selectedCustomMap && this.selectedCustomMap.AuthCredentials) {
        customMapCredentials = this.selectedCustomMap.AuthCredentials;
      }

      const securityType = customMapCredentials ? SecurityType.Password : SecurityType.Public;

      this.wmsFormGroup.get('securityType').setValue(securityType);
      this.wmsFormGroup
        .get('Username')
        .setValue(customMapCredentials && customMapCredentials.Username ? customMapCredentials.Username : '');

      this.wmsFormGroup.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    }

    this.initSecurityTypes();

    this.wmsFormGroup
      .get('Layers')
      .valueChanges.pipe(
        tap(layerName => this.setStylesFromSelectedLayers(layerName)),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.filteredLayersOptions$ = this.wmsFormGroup.get('Layers').valueChanges.pipe(
      startWith(''),
      map(value => this.filterLayers(value || '')),
    );
  }

  initSecurityTypes() {
    this.securityTypes = [
      {
        name: this.translationMap.get('public'),
        value: SecurityType.Public,
      },
      {
        name: this.translationMap.get('password'),
        value: SecurityType.Password,
      },
    ];
  }

  initWMSMapForm() {
    this.wmsFormGroup = new UntypedFormGroup({
      securityType: new UntypedFormControl(SecurityType.Public),
      Username: new UntypedFormControl(''),
      Password: new UntypedFormControl(''),
      BaseUrl: new UntypedFormControl('', [Validators.required]),
      Layers: new UntypedFormControl('', [Validators.required]),
      Version: new UntypedFormControl('1.3.0', [Validators.required]),
      ImageFormat: new UntypedFormControl('', [Validators.required]),
      Styles: new UntypedFormControl('', [Validators.required]),
    });

    /*For form valid true on switch form between different map types*/

    if (!this.isUpdateMode) {
      this.onWMSDataChanged.next({ data: {}, isFormValid: false });
    }
  }

  onChangeFormValue() {
    this.wmsFormGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      let wmsDataForCustomMap;

      if (this.isWMSPassword()) {
        wmsDataForCustomMap = {
          data: {
            BaseUrl: value.BaseUrl,
            ImageFormat: value.ImageFormat,
            Layers: value.Layers,
            Version: value.Version,
            AuthCredentials: {
              Username: value.Username,
              Password: value.Password === '' ? null : value.Password,
            },
          },
          isFormValid: this.wmsFormGroup.valid && !this.isGetLayersError,
        };
      } else {
        wmsDataForCustomMap = {
          data: {
            BaseUrl: value.BaseUrl,
            ImageFormat: value.ImageFormat,
            Layers: value.Layers,
            Version: value.Version,
          },
          isFormValid: this.wmsFormGroup.valid && !this.isGetLayersError,
        };
      }

      if (value.Styles) {
        wmsDataForCustomMap.data.Styles = value.Styles;
      }

      this.onWMSDataChanged.next(wmsDataForCustomMap);
    });
  }

  getFieldsValue() {
    let data: WmsFieldsRequestData = {
      BaseUrl: this.wmsFormGroup.get('BaseUrl').value,
      Version: this.wmsFormGroup.get('Version').value,
    };

    if (this.isWMSPassword()) {
      data = {
        ...data,
        Credentials: {
          Username: this.wmsFormGroup.get('Username').value,
          Password: this.wmsFormGroup.get('Password').value,
        },
      };
    }

    this.isShowLayersDependsFields = false;
    this.isLayersDependsFieldsDataLoading = true;

    if (!this.isUpdateMode) {
      this.customMapsService
        .getWmsFieldsValueWhileCreateMap(data)
        .pipe(
          tap(value => {
            this.setLayersDependsFieldsValue(value);

            this.isShowLayersDependsFields = true;

            this.isGetLayersError = false;

            this.cdr.markForCheck();
          }),
          catchError(error => this.handleError$(error)),
          finalize(() => (this.isLayersDependsFieldsDataLoading = false)),
          takeUntil(this.destroy$),
        )
        .subscribe();
    } else {
      const companySlug = this.selectedCustomMap.CompanySlug;

      const mapTileProviderSlug = this.selectedCustomMap.Slug;

      this.customMapsService
        .getWmsFieldsValueWhileEditMap(companySlug, mapTileProviderSlug, data)
        .pipe(
          tap(value => {

            this.setLayersDependsFieldsValue(value);

            this.isShowLayersDependsFields = true;

            this.isGetLayersError = false;

            this.cdr.markForCheck();
          }),
          catchError(error => this.handleError$(error)),
          finalize(() => (this.isLayersDependsFieldsDataLoading = false)),
          takeUntil(this.destroy$),
        )
        .subscribe();
    }
  }

  private filterLayers(value: string): LayerFieldOptionWms[] {
    const filterValue = value.toLowerCase();

    return this.layersOptions.filter(option => option.Title.toLowerCase().includes(filterValue));
  }

  private setLayersDependsFieldsValue(value: LayerDependsWmsFieldsData) {

    this.layersOptions = value.Layers;

    this.imageFormats = value.ImageFormat.Values;

    this.wmsFormGroup.get('ImageFormat').setValue(value.ImageFormat.DefaultValue);

    if (this.isUpdateMode) {
      const layerName = this.wmsFormGroup.get('Layers').value;

      this.setStylesFromSelectedLayers(layerName);
    }
  }

  private setStylesFromSelectedLayers(layerName: string | null) {
    if (layerName === null || layerName.length === 0) {
      this.wmsFormGroup.get('Styles').reset();

      return;
    }

    const selectedLayer = this.layersOptions.find(layer => layer.Name === layerName);

    this.selectedLayerChanged.emit(selectedLayer);

    const layerStyles = selectedLayer.Style;

    this.styles = layerStyles.Values;

    this.wmsFormGroup.get('Styles').setValue(layerStyles.DefaultValue);
  }

  private handleError$(error: HttpErrorResponse): Observable<HttpErrorResponse> {
    this.isGetLayersError = true;

    if (error.error.Message) {
      this.errorText = error.error.Message;
    }

    this.cdr.markForCheck();

    return of(error);
  }

  isWMSPassword() {
    return this.wmsFormGroup.get('securityType').value === SecurityType.Password;
  }

  isGetLayersButtonDisabled() {
    if (this.isWMSPassword()) {
      return !this.wmsFormGroup.get('Username').valid || !this.wmsFormGroup.get('Password').valid || this.isLayersDependsFieldsDataLoading;
    }

    return !this.wmsFormGroup.get('Version').valid || !this.wmsFormGroup.get('BaseUrl').valid || this.isLayersDependsFieldsDataLoading;
  }

  isShowStylesField() {
    return this.wmsFormGroup.get('Layers').value !== null;
  }

  displayFn(layerName: string): string {
    const selectedLayer = this.layersOptions.find(layerOption => layerOption.Name === layerName);

    return selectedLayer ? selectedLayer.Title : '';
  }

  getControlRequiredError(controlName: string): boolean {
    const control = this.wmsFormGroup.get(controlName);

    if (control === null) {
      return false;
    }

    return control.errors && control.errors.required && (control.dirty || control.touched);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
